import { create } from 'zustand';
import { AdapterType } from '@k-ucap/shared';
import type { PacketEntry, IntegrationStats, MessageInstance } from '@k-ucap/shared';

export interface ActiveAdapterState {
  id: string;
  name: string;
  type: AdapterType;
  status: 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error';
  stats: IntegrationStats;
  lastError?: string;
}

export interface IntegrationState {
  adapters: Record<string, ActiveAdapterState>;
  packets: PacketEntry[];
  maxPackets: number;
  isImporting: boolean;
  importError: string | null;

  registerAdapter: (id: string, name: string, type: AdapterType) => void;
  unregisterAdapter: (id: string) => void;
  updateAdapterStatus: (id: string, status: ActiveAdapterState['status'], error?: string) => void;
  updateAdapterStats: (id: string, patch: Partial<IntegrationStats>) => void;
  recordPacket: (packet: PacketEntry) => void;
  clearPackets: () => void;
  setMaxPackets: (max: number) => void;
  setImporting: (isImporting: boolean, error?: string | null) => void;
  getAdapter: (id: string) => ActiveAdapterState | undefined;
  getAllAdapters: () => ActiveAdapterState[];
  getPackets: (limit?: number) => PacketEntry[];
  getTotalStats: () => IntegrationStats;
  recordMessage: (id: string, direction: 'RX' | 'TX', message: MessageInstance, status?: PacketEntry['status']) => void;
}

const createEmptyStats = (): IntegrationStats => ({
  rxCount: 0,
  txCount: 0,
  rxBytes: 0,
  txBytes: 0,
  errors: 0,
  reconnects: 0,
});

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  adapters: {},
  packets: [],
  maxPackets: 500,
  isImporting: false,
  importError: null,

  registerAdapter: (id, name, type) => {
    set((state) => ({
      adapters: {
        ...state.adapters,
        [id]: {
          id,
          name,
          type,
          status: 'idle',
          stats: createEmptyStats(),
        },
      },
    }));
  },

  unregisterAdapter: (id) => {
    set((state) => {
      const next = { ...state.adapters };
      delete next[id];
      return { adapters: next };
    });
  },

  updateAdapterStatus: (id, status, error) => {
    set((state) => {
      const adapter = state.adapters[id];
      if (!adapter) return state;
      return {
        adapters: {
          ...state.adapters,
          [id]: {
            ...adapter,
            status,
            lastError: error ?? adapter.lastError,
          },
        },
      };
    });
  },

  updateAdapterStats: (id, patch) => {
    set((state) => {
      const adapter = state.adapters[id];
      if (!adapter) return state;
      return {
        adapters: {
          ...state.adapters,
          [id]: {
            ...adapter,
            stats: { ...adapter.stats, ...patch },
          },
        },
      };
    });
  },

  recordPacket: (packet) => {
    set((state) => {
      const next = [packet, ...state.packets];
      if (next.length > state.maxPackets) {
        next.length = state.maxPackets;
      }
      return { packets: next };
    });
  },

  clearPackets: () => set({ packets: [] }),

  setMaxPackets: (max) =>
    set((state) => ({
      maxPackets: max,
      packets: state.packets.slice(0, max),
    })),

  setImporting: (isImporting, error) => set({ isImporting, importError: error ?? null }),

  getAdapter: (id) => get().adapters[id],

  getAllAdapters: () => Object.values(get().adapters),

  getPackets: (limit) => {
    const { packets } = get();
    return limit ? packets.slice(0, limit) : packets;
  },

  getTotalStats: () => {
    const { adapters } = get();
    return Object.values(adapters).reduce(
      (total, adapter) => ({
        rxCount: total.rxCount + adapter.stats.rxCount,
        txCount: total.txCount + adapter.stats.txCount,
        rxBytes: total.rxBytes + adapter.stats.rxBytes,
        txBytes: total.txBytes + adapter.stats.txBytes,
        errors: total.errors + adapter.stats.errors,
        reconnects: total.reconnects + adapter.stats.reconnects,
      }),
      createEmptyStats()
    );
  },

  recordMessage: (id, direction, message, status = 'ok') => {
    const { registerAdapter, recordPacket, updateAdapterStats } = get();
    if (!get().adapters[id]) {
      registerAdapter(id, id, AdapterType.VIRTUAL);
    }
    const data = JSON.stringify(message);
    const size = new Blob([data]).size;
    recordPacket({
      id: `${id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      direction,
      adapterName: id,
      adapterType: AdapterType.VIRTUAL,
      source: message.source,
      target: message.target,
      messageType: message.messageType,
      messageName: message.name,
      sizeBytes: size,
      status,
      metadata: {
        projectId: message.projectId,
        environment: message.environment,
        runId: message.runId,
      },
    });
    updateAdapterStats(id, {
      [direction === 'RX' ? 'rxCount' : 'txCount']:
        (get().adapters[id]?.stats[direction === 'RX' ? 'rxCount' : 'txCount'] ?? 0) + 1,
      [direction === 'RX' ? 'rxBytes' : 'txBytes']:
        (get().adapters[id]?.stats[direction === 'RX' ? 'rxBytes' : 'txBytes'] ?? 0) + size,
    });
  },
}));

export default useIntegrationStore;
